
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent 
} from '@/components/ui/chart';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  LineChart, 
  Line, 
  ResponsiveContainer 
} from 'recharts';
import { AdminSubmission } from '@/hooks/useAdminData';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';

interface AdminAnalyticsProps {
  submissions: AdminSubmission[];
}

const AdminAnalytics = ({ submissions }: AdminAnalyticsProps) => {
  // Business type distribution
  const businessTypeData = React.useMemo(() => {
    const counts: Record<string, number> = {};
    submissions.forEach(sub => {
      counts[sub.business_type] = (counts[sub.business_type] || 0) + 1;
    });
    
    return Object.entries(counts).map(([type, count]) => ({
      name: type,
      value: count,
      percentage: Math.round((count / submissions.length) * 100)
    }));
  }, [submissions]);

  // Energy score distribution
  const scoreDistribution = React.useMemo(() => {
    const ranges = [
      { range: '0-20', min: 0, max: 20 },
      { range: '21-40', min: 21, max: 40 },
      { range: '41-60', min: 41, max: 60 },
      { range: '61-80', min: 61, max: 80 },
      { range: '81-100', min: 81, max: 100 }
    ];

    return ranges.map(({ range, min, max }) => ({
      range,
      count: submissions.filter(sub => 
        sub.energy_potential_score && 
        sub.energy_potential_score >= min && 
        sub.energy_potential_score <= max
      ).length
    }));
  }, [submissions]);

  // Submissions over time (last 6 months)
  const timeData = React.useMemo(() => {
    const months = [];
    const now = new Date();
    
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('nl-NL', { month: 'short' });
      const count = submissions.filter(sub => {
        const subDate = new Date(sub.created_at);
        return subDate.getMonth() === date.getMonth() && 
               subDate.getFullYear() === date.getFullYear();
      }).length;
      
      months.push({ month: monthName, submissions: count });
    }
    
    return months;
  }, [submissions]);

  const COLORS = ['#2D5530', '#00B4DB', '#FF7F00', '#4ADE80', '#38BDF8', '#FB923C'];

  const chartConfig = {
    businessType: {
      label: "Bedrijfstype",
    },
    score: {
      label: "Energy Score",
    },
    timeline: {
      label: "Submissions",
    },
  };

  return (
    <div className="grid gap-6">
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Business Type Distribution */}
        <Card className="flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Bedrijfstype Verdeling</CardTitle>
            <CardDescription className="text-sm">Distributie van verschillende bedrijfstypes</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col">
            <div className="flex-1 min-h-0">
              <ChartContainer config={chartConfig} className="h-[200px] w-full">
                <PieChart>
                  <Pie
                    data={businessTypeData}
                    cx="50%"
                    cy="50%"
                    outerRadius="80%"
                    dataKey="value"
                    label={({ percentage }) => `${percentage}%`}
                    labelLine={false}
                  >
                    {businessTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip content={<ChartTooltipContent />} />
                </PieChart>
              </ChartContainer>
            </div>
            <div className="mt-3 space-y-1.5 max-h-32 overflow-y-auto">
              {businessTypeData.map((item, index) => (
                <div key={item.name} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <div 
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="truncate">{item.name}</span>
                  </div>
                  <Badge variant="outline" className="text-xs py-0 px-1.5 ml-2">{item.value}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Energy Score Distribution */}
        <Card className="flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Energy Score Verdeling</CardTitle>
            <CardDescription className="text-sm">Distributie van energy potential scores</CardDescription>
          </CardHeader>
          <CardContent className="flex-1">
            <ChartContainer config={chartConfig} className="h-[250px] w-full">
              <BarChart data={scoreDistribution} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                <XAxis 
                  dataKey="range" 
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={30}
                />
                <Bar 
                  dataKey="count" 
                  fill="#2D5530" 
                  radius={[2, 2, 0, 0]}
                  maxBarSize={40}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card className="flex flex-col">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Submissions Trend</CardTitle>
            <CardDescription className="text-sm">Submissions over de laatste 6 maanden</CardDescription>
          </CardHeader>
          <CardContent className="flex-1">
            <ChartContainer config={chartConfig} className="h-[250px] w-full">
              <LineChart data={timeData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={30}
                />
                <Line 
                  type="monotone" 
                  dataKey="submissions" 
                  stroke="#00B4DB" 
                  strokeWidth={2}
                  dot={{ fill: '#00B4DB', strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 4, fill: '#00B4DB' }}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers */}
      <Card>
        <CardHeader>
          <CardTitle>Top Performing Submissions</CardTitle>
          <CardDescription>Submissions met de hoogste energy potential scores</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {submissions
              .filter(sub => sub.energy_potential_score)
              .sort((a, b) => (b.energy_potential_score || 0) - (a.energy_potential_score || 0))
              .slice(0, 5)
              .map(submission => (
                <div key={submission.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="font-semibold">{submission.company_name || 'Geen bedrijfsnaam'}</div>
                    <div className="text-sm text-gray-500">{submission.business_type}</div>
                    <div className="mt-2">
                      <Progress value={submission.energy_potential_score || 0} className="h-2" />
                    </div>
                  </div>
                  <div className="ml-4 text-right">
                    <Badge 
                      variant={(submission.energy_potential_score || 0) >= 80 ? "default" : "secondary"}
                      className="text-lg font-bold"
                    >
                      {submission.energy_potential_score}%
                    </Badge>
                    <div className="text-sm text-gray-500 mt-1">
                      €{submission.yearly_annual_savings?.toLocaleString() || 'N/A'}/jaar
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAnalytics;
